import { Bot, CheckCircle2, Flag, PartyPopper, Target } from "lucide-react";
import { useCanAccess, useGetIdentity, useGetList } from "ra-core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { cn } from "@/lib/utils";

import { ClientSheet } from "../ClientSheet";
import type { GulaClient } from "../gulaTypes";
import { SHIFTS, type ShiftKey } from "../shifts";
import { TeamChat } from "../team/TeamChat";
import { currentCallSlot, useAgents } from "../useAgents";
import {
  MY_DAY_PATH,
  OUTCOME_LABEL,
  buildBuckets,
  missionName,
  progressStage,
  rankingLine,
  sortMissions,
  type CallMission,
} from "./callPlan";
import type { ContactNotes } from "../notes/clientNotes";
import { useClientNotes } from "../notes/useClientNotes";
import { ClientSidePanel } from "./ClientSidePanel";
import { MissionRow, NextCall, NotesButton, RowNotes } from "./MissionRow";
import { RankingCard } from "./RankingCard";
import { SocialInboxCards } from "./SocialInboxCards";
import { TakeWorkCard } from "./TakeWorkCard";
import { NewLeadsNow } from "./NewLeadAlerts";
import { TodayPodium } from "./TodayPodium";
import { useAutoPilot } from "./useAutoPilot";
import { useCallPlan } from "./useCallPlan";
import { useMissionActions } from "./useMissionActions";
import { WelcomeCallsCard } from "../welcome/WelcomeCallsCard";

const MILESTONES = [0.25, 0.5, 0.75, 1];

/** Wide screens get the client panel docked next to the list. */
const WIDE_QUERY = "(min-width: 1024px)";
const useIsWide = () => {
  const [wide, setWide] = useState(
    () =>
      typeof window !== "undefined" && window.matchMedia(WIDE_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return wide;
};

const longDate = (date: Date) => {
  const text = date.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Progress bar against the goal with four checkpoints. */
const GoalTrack = ({ calls, goal }: { calls: number; goal: number }) => {
  const ratio = Math.min(1, goal ? calls / goal : 0);
  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-3 rounded-full bg-white/70">
        <div
          className="h-full rounded-full bg-neutral-900 transition-all duration-700"
          style={{ width: `${ratio * 100}%` }}
        />
        {MILESTONES.map((milestone) => (
          <span
            key={milestone}
            className={cn(
              "absolute top-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 text-[10px] font-bold",
              ratio >= milestone
                ? "border-neutral-900 bg-neutral-900 text-lime-300"
                : "border-white bg-lime-100 text-neutral-500",
            )}
            style={{ left: `${milestone * 100}%` }}
          >
            {milestone === 1 ? (
              <Flag className="h-3 w-3" />
            ) : (
              Math.round(goal * milestone)
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

/** A call already made, crossed out, still open to leave a note after it. */
const DoneRow = ({
  mission,
  notes,
  onNotesChanged,
  onOpenFicha,
}: {
  mission: CallMission;
  notes: ContactNotes;
  onNotesChanged: () => void;
  onOpenFicha: (mission: CallMission) => void;
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  return (
    <li className="flex flex-col gap-1 rounded-xl px-2 py-1.5 text-sm">
      <div className="flex items-center gap-2">
        <CheckCircle2
          className={cn(
            "h-4 w-4 shrink-0",
            mission.outcome === "skip"
              ? "text-muted-foreground"
              : "text-lime-600",
          )}
        />
        <button
          type="button"
          onClick={() => onOpenFicha(mission)}
          className="flex-1 truncate text-left text-muted-foreground line-through hover:text-foreground"
          title="Abrir ficha"
        >
          {missionName(mission)}
        </button>
        <span className="shrink-0 text-xs font-medium">
          {OUTCOME_LABEL[mission.outcome ?? ""] ?? ""}
        </span>
        <NotesButton
          count={notes.team.length}
          open={notesOpen}
          onClick={() => setNotesOpen(!notesOpen)}
        />
      </div>
      {notesOpen ? (
        <RowNotes
          contactId={mission.contact_id}
          notes={notes}
          open
          onOpen={() => setNotesOpen(true)}
          onChanged={onNotesChanged}
        />
      ) : null}
    </li>
  );
};

/**
 * Mi jornada: each user's own list for today, worked one call at a time, with
 * the goal, the ranking and more work to take when the list runs out.
 */
export const MyDayPage = () => {
  const { identity } = useGetIdentity();
  // Admins see every welcome call, not only their own
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "team",
    action: "list",
  });
  const { agents, meId } = useAgents();
  const { missions, ranking, reload } = useCallPlan();
  const { data: clients = [], refetch: refetchClients } =
    useGetList<GulaClient>(
      "gula_clients_summary",
      {
        pagination: { page: 1, perPage: 2000 },
        sort: { field: "created_at", order: "DESC" },
      },
      { refetchInterval: 60_000 },
    );
  const onChanged = useCallback(() => {
    reload();
    refetchClients();
  }, [reload, refetchClients]);
  // Whoever opens the panel to sell gets the list filled (setters, Mica or
  // Joaquín); leads left untouched for an hour go back to the pool
  const noWork = !!agents.find((agent) => String(agent.id) === String(meId))
    ?.no_work;
  const autoPilot = useAutoPilot({
    defaultOn: true,
    blocked: noWork,
    clients,
    missions,
    meId,
    onChanged,
  });
  const { busyId, handleResult, handleRelease } = useMissionActions(onChanged);

  // Client panel ("Ficha") opened from the list; kept in the URL like in Clientes
  const [searchParams, setSearchParams] = useSearchParams();
  const fichaId = searchParams.get("cliente");
  const setFichaId = (id: string | null) =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (id) params.set("cliente", id);
        else params.delete("cliente");
        return params;
      },
      { replace: true },
    );
  const openFicha = (mission: CallMission) =>
    setFichaId(
      fichaId === String(mission.client_id) ? null : String(mission.client_id),
    );
  const fichaClient = fichaId
    ? (clients.find((client) => String(client.id) === fichaId) ?? null)
    : null;
  const wide = useIsWide();
  const docked = wide && !!fichaClient;
  const isOpen = (mission: CallMission) =>
    fichaId === String(mission.client_id);

  const slot = currentCallSlot();
  const waiting = useMemo(
    () =>
      buildBuckets({
        clients,
        claimedClientIds: new Set(
          missions.map((mission) => String(mission.client_id)),
        ),
        meId,
        shift: (slot as ShiftKey | null) ?? "all",
      }).reduce((sum, bucket) => sum + bucket.clients.length, 0),
    [clients, missions, meId, slot],
  );
  const mine = sortMissions(
    missions.filter((mission) => String(mission.sales_id) === String(meId)),
    slot,
  );
  const open = mine.filter((mission) => !mission.done_at);
  const done = mine.filter((mission) => mission.done_at);
  const [next, ...queue] = open;
  const { notesOf, refetch: refetchNotes } = useClientNotes(
    mine.map((mission) => mission.contact_id),
  );
  const row = ranking.find((item) => String(item.id) === String(meId));
  const goal = row?.goal ?? 25;
  const calls = row?.today_calls ?? 0;
  const reached = calls >= goal;
  const shiftLabel = SHIFTS.find((shift) => shift.key === slot)?.label;
  const firstName = identity?.fullName?.split(" ")[0] ?? "";
  const listProgress = mine.length ? done.length / mine.length : 0;

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <Target className="h-7 w-7" />
            Mi jornada
          </h1>
          <p className="text-sm text-muted-foreground">
            {firstName ? `${firstName} · ` : ""}
            {longDate(new Date())}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "rounded-full px-3 py-1.5 text-sm font-medium",
              shiftLabel
                ? "bg-lime-200 text-neutral-900"
                : "bg-muted text-muted-foreground",
            )}
          >
            {shiftLabel
              ? `Turno de ${shiftLabel.toLowerCase()} · ahora`
              : "Fuera de horario de llamadas"}
          </span>
          {rankingLine(ranking, meId) ? (
            <span className="rounded-full bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-white dark:text-neutral-900">
              {rankingLine(ranking, meId)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div
          className={cn(
            "flex flex-col gap-6",
            docked ? "lg:col-span-7" : "lg:col-span-8",
          )}
        >
          <NewLeadsNow />
          <section className="flex flex-col gap-5 rounded-3xl bg-lime-200 p-6 text-neutral-900">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-neutral-700">
                  {progressStage(calls, goal)}
                </span>
                <span className="text-5xl font-bold tracking-tight">
                  {calls}
                  <span className="text-2xl font-semibold text-neutral-600">
                    {" "}
                    / {goal} llamadas
                  </span>
                </span>
              </div>
              <div className="flex gap-2">
                {[
                  ["Hablados hoy", row?.today_contacts ?? 0],
                  ["Ventas hoy", row?.today_sales ?? 0],
                  ["Puntos hoy", row?.today_points ?? 0],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex min-w-20 flex-col rounded-2xl bg-white/60 px-3 py-2"
                  >
                    <span className="text-xl font-semibold leading-tight">
                      {value}
                    </span>
                    <span className="text-xs text-neutral-600">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <GoalTrack calls={calls} goal={goal} />
            {reached ? (
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1.5 text-sm font-medium text-lime-300">
                <PartyPopper className="h-4 w-4" />
                Objetivo de hoy cumplido. Todo lo que sumes ahora es extra.
              </span>
            ) : null}
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">Mi lista de hoy</h2>
              <span className="text-sm text-muted-foreground">
                {done.length} de {mine.length} hechas
              </span>
            </div>
            <label className="flex cursor-pointer flex-wrap items-center gap-2 rounded-2xl border px-3 py-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-lime-500"
                checked={autoPilot.enabled}
                disabled={noWork}
                onChange={(event) => autoPilot.toggle(event.target.checked)}
              />
              <Bot className="h-4 w-4 text-lime-600" />
              <span className="font-medium">Piloto automático</span>
              <span className="text-muted-foreground">
                {noWork
                  ? "perfil de prueba: no recibe leads ni tareas"
                  : autoPilot.enabled
                    ? `te pone solo la siguiente llamada con más probabilidad de cierre · ${waiting} esperando en ${shiftLabel ? `el turno de ${shiftLabel.toLowerCase()}` : "la bolsa"}`
                    : `actívalo y la lista se llena sola · ${waiting} esperando`}
              </span>
            </label>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-lime-400 transition-all duration-500"
                style={{ width: `${listProgress * 100}%` }}
              />
            </div>

            {next ? (
              <div className="rounded-3xl bg-lime-100 p-2 dark:bg-lime-300/15">
                <NextCall
                  mission={next}
                  busy={busyId === String(next.id)}
                  onResult={handleResult}
                  onRelease={handleRelease}
                  notes={notesOf(next.contact_id)}
                  onNotesChanged={refetchNotes}
                  onOpenFicha={openFicha}
                  fichaOpen={isOpen(next)}
                />
              </div>
            ) : (
              <div className="flex flex-col items-start gap-1 rounded-3xl border border-dashed p-6">
                <span className="text-lg font-semibold">
                  {done.length ? "¡Lista completada!" : "Tu lista está vacía"}
                </span>
                <span className="text-sm text-muted-foreground">
                  {done.length
                    ? "Buen trabajo. Coge más faena a la derecha y sigue sumando."
                    : autoPilot.enabled
                      ? "El piloto automático te está buscando las mejores llamadas…"
                      : "Coge faena a la derecha, o pulsa «Coger» en un lead del tablero de Clientes."}
                </span>
              </div>
            )}

            {queue.length ? (
              <div className="flex flex-col gap-2 rounded-3xl bg-muted/50 p-3">
                <span className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  En cola · {queue.length}
                </span>
                <ol className="flex flex-col gap-1.5">
                  {queue.map((mission) => (
                    <MissionRow
                      key={mission.id}
                      mission={mission}
                      busy={busyId === String(mission.id)}
                      onResult={handleResult}
                      onRelease={handleRelease}
                      notes={notesOf(mission.contact_id)}
                      onNotesChanged={refetchNotes}
                      onOpenFicha={openFicha}
                      fichaOpen={isOpen(mission)}
                    />
                  ))}
                </ol>
              </div>
            ) : null}

            {done.length ? (
              <div className="flex flex-col gap-1.5 rounded-3xl border p-3">
                <span className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Hechas · {done.length}
                </span>
                <ul className="flex flex-col gap-1">
                  {done.map((mission) => (
                    <DoneRow
                      key={mission.id}
                      mission={mission}
                      notes={notesOf(mission.contact_id)}
                      onNotesChanged={refetchNotes}
                      onOpenFicha={openFicha}
                    />
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <SocialInboxCards />

          <WelcomeCallsCard
            meId={meId}
            isAdmin={isAdmin}
            onChanged={onChanged}
          />
        </div>

        {docked && fichaClient ? (
          <div className="lg:col-span-5">
            <ClientSidePanel
              client={fichaClient}
              onClose={() => setFichaId(null)}
              onChanged={onChanged}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-6 lg:col-span-4">
            <TodayPodium ranking={ranking} meId={meId} />
            <TakeWorkCard
              title={autoPilot.enabled ? "Elegir a mano" : "Coge más faena"}
              onlyMe
              clients={clients}
              missions={missions}
              agents={agents}
              meId={meId}
              onChanged={onChanged}
            />
            <TeamChat agents={agents} clients={clients} />
            <RankingCard ranking={ranking} meId={meId} onChanged={onChanged} />
          </div>
        )}
      </div>

      {/* Phones and small screens: the same panel slides in over the page */}
      {!wide ? (
        <ClientSheet
          client={fichaClient}
          onClose={() => setFichaId(null)}
          onSaved={onChanged}
          showStageBar
        />
      ) : null}
    </div>
  );
};

MyDayPage.path = MY_DAY_PATH;
